import { ContentLoading } from "../components/ContentLoading";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { OpsCustomerSummary, OpsPermission } from "../../shared/operations-schemas";
import { FieldError } from "../components/ui/field";
import { billingQueryKey, getBilling } from "./billing-api";
import { BillingActivity } from "./BillingActivity";
import { BillingAgreements } from "./BillingAgreements";
import { BillingRecords } from "./BillingRecords";
import { BillingStripeCard } from "./BillingStripeCard";
import { CustomerBillingSummary } from "./CustomerBillingSummary";
import { EmptyCard } from "./PageSection";

/**
 * The Billing tab of a customer (ADR-0073, ADR-0075).
 *
 * Three permissions gate three layers. `billing.read` shows everything; `billing.manage` drafts
 * quotes, edits terms, and records money collected elsewhere; `billing.collect` moves money
 * through Stripe. The tab renders what the employee may do and nothing they may not, and the
 * server refuses the rest regardless. Quotes come before terms because that is the normal path:
 * a quote the customer accepts becomes the terms; the employee's Approve stays for the rest.
 */

export function CustomerBilling({
  customer,
  permissions,
}: {
  customer: OpsCustomerSummary;
  permissions: ReadonlySet<OpsPermission>;
}) {
  const canRead = permissions.has("billing.read");
  const canManage = permissions.has("billing.manage");
  const canCollect = permissions.has("billing.collect");
  const queryClient = useQueryClient();
  const billingQuery = useQuery({
    queryKey: billingQueryKey(customer.slug),
    queryFn: () => getBilling(customer.slug),
    enabled: canRead,
  });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: billingQueryKey(customer.slug) });
  };
  if (!canRead)
    return <EmptyCard>This employee account cannot read this customer&rsquo;s billing.</EmptyCard>;
  if (billingQuery.isPending) return <ContentLoading label="Loading billing…" variant="cards" />;
  if (billingQuery.error) return <FieldError>{billingQuery.error.message}</FieldError>;
  const billing = billingQuery.data?.billing;
  if (!billing) return null;
  // Sections stack with gap, not space-y: a child's own margin must never close the gap (ADR-0087).
  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-4">
        <CustomerBillingSummary customer={customer} />
        <BillingStripeCard
          customer={customer}
          billing={billing}
          canCollect={canCollect}
          canReadContacts={permissions.has("context.read")}
          refresh={refresh}
        />
      </div>
      <BillingAgreements
        customerSlug={customer.slug}
        billing={billing}
        canManage={canManage}
        canCollect={canCollect}
        refresh={refresh}
      />
      <BillingRecords
        customerSlug={customer.slug}
        billing={billing}
        canManage={canManage}
        canCollect={canCollect}
        onChanged={refresh}
      />
      <BillingActivity customerSlug={customer.slug} />
    </div>
  );
}
